import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, Input } from "@/design-system";
import { Icon } from "@/components/Icon";
import { ApiRequestError, api } from "@/api/client";
import { useAuth } from "@/auth";
import type { User } from "@/lib/types";

/**
 * Profile card for the account settings screen (issue #224).
 *
 * Lets the signed-in user (teacher or student) edit their own display
 * name via `PATCH /api/me`. Deliberately **orthogonal to the password
 * change**: no password is required and no session token is rotated —
 * the response is the updated user, which `useAuth().updateUser` folds
 * back into the context so every greeting updates immediately.
 *
 * Email and role are not editable here (email has its own confirmation
 * flow; role is never user-writable), and the server ignores them even
 * if they were sent.
 */
export function ProfileForm() {
  const { token, currentUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [name, setName] = useState(currentUser?.name ?? "");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation<User, Error, string>({
    mutationFn: (nextName) =>
      api.patch<User>("/api/me", { name: nextName }, { token }),
  });

  const isPending = mutation.isPending;

  // Prefill once the session hydrates (the form can render before
  // `GET /api/me` resolves). Keyed on the id so a stale name from a
  // previous account can never leak in.
  useEffect(() => {
    setName(currentUser?.name ?? "");
  }, [currentUser?.id, currentUser?.name]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (isPending) return;

    const trimmed = name.trim();
    if (trimmed === "") {
      setFieldError(t("settings.errorRequired"));
      setGeneralError(null);
      setSuccess(false);
      return;
    }
    if (trimmed.length > 160) {
      setFieldError(t("settings.nameTooLong"));
      setGeneralError(null);
      setSuccess(false);
      return;
    }

    setFieldError(null);
    setGeneralError(null);
    setSuccess(false);

    mutation.mutate(trimmed, {
      onSuccess: (updated) => {
        updateUser(updated);
        setSuccess(true);
      },
      onError: (err) => {
        if (err instanceof ApiRequestError && err.status === 401) {
          setGeneralError(t("settings.errorSessionExpired"));
          navigate("/login", { replace: true });
          return;
        }
        if (err instanceof ApiRequestError && err.status === 422) {
          setFieldError(
            pickFirstMessage(err.body?.errors?.name) ?? t("settings.nameError"),
          );
          return;
        }
        setGeneralError(
          err instanceof ApiRequestError ? err.message : t("settings.errorGeneric"),
        );
      },
    });
  }

  function clearErrorsOnEdit() {
    if (fieldError || generalError) {
      setFieldError(null);
      setGeneralError(null);
    }
    if (success) setSuccess(false);
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <Input
        label={t("settings.nameLabel")}
        name="name"
        autoComplete="name"
        placeholder={t("settings.namePlaceholder")}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          clearErrorsOnEdit();
        }}
        required
        maxLength={160}
        disabled={isPending || !currentUser}
        invalid={Boolean(fieldError)}
        hint={fieldError ?? t("settings.nameHint")}
        iconLeft={<Icon name="user" size={18} />}
        data-testid="profile-name"
      />

      {generalError && (
        <Card
          role="alert"
          padding="10px 14px"
          style={{
            background: "var(--status-danger-bg)",
            border: "1px solid var(--rust-500)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--status-danger-fg)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
              <Icon name="flame" size={18} />
            </span>
            {generalError}
          </div>
        </Card>
      )}

      {success && (
        <Card
          role="status"
          padding="10px 14px"
          style={{
            background: "var(--status-success-bg)",
            border: "1px solid var(--sage-600)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--status-success-fg)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            <span style={{ display: "inline-flex", color: "var(--sage-600)" }}>
              <Icon name="check" size={18} />
            </span>
            {t("settings.nameSuccess")}
          </div>
        </Card>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={isPending || !currentUser}
        data-testid="profile-submit"
      >
        {isPending ? t("settings.savingName") : t("settings.saveName")}
      </Button>
    </form>
  );
}

/** Pull a human-readable message out of a string[] or a string. */
function pickFirstMessage(raw: unknown): string | undefined {
  if (Array.isArray(raw)) {
    const first = raw[0];
    return typeof first === "string" ? first : undefined;
  }
  if (typeof raw === "string") return raw;
  return undefined;
}
